import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle2, Sparkles } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { GameBoard } from "@/components/GameBoard";
import { LetterTile } from "@/components/LetterTile";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { celebrateSmall } from "@/lib/confetti";

const LETTER_VALUES = {
  A: 1, E: 1, I: 1, L: 1, N: 1, O: 1, R: 1, S: 1, T: 1, U: 1,
  D: 2, G: 2, M: 2, B: 3, C: 3, P: 3, F: 4, H: 4, V: 4,
  J: 8, Q: 8, K: 10, W: 10, X: 10, Y: 10, Z: 10, "?": 0,
};

const EMPTY_BOARD = () => Array.from({ length: 6 }, () => Array.from({ length: 9 }, () => null));

/**
 * Interactive Tutorial — 6 progressive steps: board, letters, numbers, zones,
 * turn, scoring, end of game. Each step ends with the user performing an action.
 */
export default function Tutorial() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [bonuses, setBonuses] = useState([]);
  const [plateau, setPlateau] = useState([]);
  const [board, setBoard] = useState(EMPTY_BOARD());
  const [placed, setPlaced] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rack] = useState(["C", "H", "A", "T", "E", "R", "S"]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/games/bonuses");
        setBonuses(data.bonuses);
        setPlateau(data.plateau || []);
      } catch (_) {}
    })();
  }, []);

  const steps = [
    { key: "board", title: t("tut.step.board.title"), body: t("tut.step.board.body") },
    { key: "letters", title: t("tut.step.letters.title"), body: t("tut.step.letters.body") },
    { key: "numbers", title: t("tut.step.numbers.title"), body: t("tut.step.numbers.body") },
    { key: "zones", title: t("tut.step.zones.title"), body: t("tut.step.zones.body") },
    { key: "turn", title: t("tut.step.turn.title"), body: t("tut.step.turn.body") },
    { key: "score", title: t("tut.step.score.title"), body: t("tut.step.score.body") },
    { key: "end", title: t("tut.step.end.title"), body: t("tut.step.end.body") },
  ];

  const current = steps[step];
  const isActionStep = current?.key === "turn";
  const targetPlaced = placed.length === 3; // CHAT would need 4 letters, but let's use CAT for simplicity
  const stepComplete = !isActionStep || targetPlaced;

  const clickCell = (r, c) => {
    if (!isActionStep || board[r][c]) return;
    const existing = placed.find((p) => p.row === r && p.col === c);
    if (existing) {
      setPlaced(placed.filter((p) => p !== existing));
      return;
    }
    if (selected == null) return;
    // Guide: enforce CAT at center row
    // C at (2,3), A at (2,4), T at (2,5)
    const expected = [{ r: 2, c: 3, l: "C" }, { r: 2, c: 4, l: "A" }, { r: 2, c: 5, l: "T" }];
    const next = expected[placed.length];
    if (!next || r !== next.r || c !== next.c) return;
    const letter = rack[selected];
    if (letter !== next.l) return;
    const newPlaced = [...placed, { row: r, col: c, letter, rackIndex: selected }];
    setPlaced(newPlaced);
    setSelected(null);
    if (newPlaced.length === 3) {
      celebrateSmall();
    }
  };

  const next = () => {
    if (step < steps.length - 1) setStep(step + 1);
    else {
      localStorage.setItem("globo:tutorial-done", "1");
      celebrateSmall();
      navigate("/play");
    }
  };
  const prev = () => setStep(Math.max(0, step - 1));

  const usedIndices = new Set(placed.map((p) => p.rackIndex));

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">
              {t("tut.badge")}
            </div>
            <h1 className="font-display font-extrabold text-4xl tracking-tight mt-1">
              {t("tut.title")}
            </h1>
          </div>
          <Button variant="ghost" onClick={() => navigate("/play")} data-testid="tut-skip-btn">
            {t("tut.skip")}
          </Button>
        </div>

        <Progress value={((step + 1) / steps.length) * 100} className="mb-2" />
        <div className="text-xs text-muted-foreground mb-6">
          {t("tut.stepLabel").replace("{n}", step + 1).replace("{total}", steps.length)}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 items-start">
          <Card className="border-border">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-primary" />
                <Badge variant="secondary" className="uppercase tracking-wider text-[10px]">
                  {t("tut.stepLabel").replace("{n}", step + 1).replace("{total}", steps.length)}
                </Badge>
              </div>
              <h2 className="font-display font-extrabold text-2xl tracking-tight mb-3" data-testid="tut-step-title">
                {current.title}
              </h2>
              <p className="text-muted-foreground leading-relaxed" data-testid="tut-step-body">
                {current.body}
              </p>

              {isActionStep && (
                <div className="mt-6 p-4 rounded-lg bg-primary/5 border border-primary/20">
                  <div className="text-sm font-medium mb-3">{t("tut.action.placeCat")}</div>
                  <div className="flex gap-2 flex-wrap">
                    {rack.map((l, i) => (
                      <LetterTile
                        key={i}
                        letter={l}
                        points={LETTER_VALUES[l]}
                        size="md"
                        selected={selected === i}
                        faded={usedIndices.has(i)}
                        onClick={usedIndices.has(i) ? undefined : () => setSelected(selected === i ? null : i)}
                        testId={`tut-rack-${i}`}
                      />
                    ))}
                  </div>
                  <div className="mt-3 text-xs text-muted-foreground">
                    {placed.length} / 3 {t("tut.action.placedCount")}
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-between">
                <Button variant="outline" onClick={prev} disabled={step === 0} data-testid="tut-prev-btn">
                  <ArrowLeft className="w-4 h-4 mr-1" /> {t("common.prev")}
                </Button>
                <Button onClick={next} disabled={!stepComplete} data-testid="tut-next-btn">
                  {step === steps.length - 1 ? (
                    <>{t("tut.finish")} <CheckCircle2 className="w-4 h-4 ml-1" /></>
                  ) : (
                    <>{t("common.next")} <ArrowRight className="w-4 h-4 ml-1" /></>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-center">
            <div className="scale-90 sm:scale-100 origin-top">
              <GameBoard
                board={board}
                bonuses={bonuses}
                plateau={plateau}
                placed={placed}
                onCellClick={isActionStep ? clickCell : undefined}
                size={44}
              />
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
